/**
 * HealthCard - one component-status card on AdminHealthPage (Phase 12.6).
 *
 * SPEC §9 color mapping:
 *   ok             -> green left border
 *   degraded       -> amber
 *   down           -> red
 *   not_configured -> neutral
 */
import { Card } from "@/components/ui";
import type { ComponentHealth, ComponentStatus } from "@/types/api";

const BORDER_CLASS: Record<ComponentStatus, string> = {
  ok: "border-l-4 border-l-emerald-500",
  degraded: "border-l-4 border-l-amber-500",
  down: "border-l-4 border-l-red-500",
  not_configured: "border-l-4 border-l-neutral-300",
};

const STATUS_LABEL: Record<ComponentStatus, string> = {
  ok: "Healthy",
  degraded: "Degraded",
  down: "Down",
  not_configured: "Not configured",
};

const STATUS_TEXT_COLOR: Record<ComponentStatus, string> = {
  ok: "text-emerald-700",
  degraded: "text-amber-700",
  down: "text-red-700",
  not_configured: "text-neutral-600",
};

const PRETTY_NAME: Record<string, string> = {
  postgres: "Postgres",
  redis: "Redis",
  // Outbound + inbound mail run through Microsoft Graph (Entra ID
  // app ``buyist-support-mailer``) since the 2026-05-14 switch from
  // SendGrid. The ``sendgrid`` key is kept as a fallback alias in
  // case any legacy health-probe response still surfaces it - the
  // user-facing label always says "Microsoft 365" now.
  microsoft_graph: "Microsoft 365",
  sendgrid: "Microsoft 365",
  spaces: "DigitalOcean Spaces",
  buyist_api: "Buyist eCommerce API",
};

export interface HealthCardProps {
  component: ComponentHealth;
}

export function HealthCard({ component }: HealthCardProps): JSX.Element {
  const displayName = PRETTY_NAME[component.name] ?? component.name;
  return (
    <Card padding="md" className={BORDER_CLASS[component.status]}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-neutral-900">
          {displayName}
        </h3>
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${
            STATUS_TEXT_COLOR[component.status]
          }`}
        >
          {STATUS_LABEL[component.status]}
        </span>
      </div>
      {component.detail && (
        <p className="mt-2 text-xs text-neutral-600">{component.detail}</p>
      )}
      {component.latency_ms !== null && (
        <p className="mt-2 text-xs text-neutral-500">
          Latency: {component.latency_ms} ms
        </p>
      )}
    </Card>
  );
}

export default HealthCard;
